
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArtworkForm } from "./ArtworkForm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { EditionTable } from "./EditionTable";
import { EditionForm } from "./EditionForm";
import { getPreviewUrl } from "@/utils/imageUtils";
import { FileUploadSection } from "@/components/digitization/FileUploadSection";

interface ArtworkDialogProps {
  artwork: {
    artwork_id: number;
    title: string;
    art_technique: string | null;
    description: string | null;
    creation_date: string | null;
    location: string | null;
    preview: string;
    verified: boolean | null;
    creator_id: number;
    highres_file: number;
  };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ArtworkDialog({ artwork, open, onOpenChange }: ArtworkDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedEdition, setSelectedEdition] = useState<{
    edition_id: number;
    print_price: number | null;
    default_media?: number | null;
  } | null>(null);

  // Fetch available media types
  const { data: mediaTypes } = useQuery({
    queryKey: ['media'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media')
        .select('*')
        .order('media_name');
      
      if (error) throw error;
      return data;
    },
  });

  const handlePrintPriceUpdate = async () => {
    if (!selectedEdition) return;

    try {
      const { data, error } = await supabase
        .from('editions')
        .update({ 
          print_price: selectedEdition.print_price,
          default_media: selectedEdition.default_media 
        })
        .eq('edition_id', selectedEdition.edition_id)
        .eq('artwork_id', artwork.artwork_id)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('You do not have permission to update this edition');

      await queryClient.invalidateQueries({ 
        queryKey: ['editions', artwork.artwork_id] 
      });

      toast({
        title: "Success",
        description: "Edition details updated successfully",
      });

      setSelectedEdition(null);
    } catch (error) {
      console.error('Error updating edition details:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update edition details",
        variant: "destructive",
      });
    }
  };

  // Add onUploadComplete function to handle the file upload completion
  const onUploadComplete = async () => {
    // Invalidate the artwork query to refresh the data
    await queryClient.invalidateQueries({ 
      queryKey: ['artworks-list'] 
    });

    // Show success toast
    toast({
      title: "Success",
      description: "Artwork file updated successfully",
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Artwork Details</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-full max-h-[calc(90vh-8rem)]">
            <div className="space-y-6">
              <div className="grid md:grid-cols-[350px,1fr] gap-6 py-4 px-1">
                <div className="space-y-4">
                  <div className="w-full h-fit rounded-lg overflow-hidden">
                    <img
                      src={getPreviewUrl(artwork.preview)}
                      alt={artwork.title}
                      className="w-full object-contain"
                    />
                  </div>
                  <FileUploadSection 
                    artworkId={artwork.artwork_id}
                    onUploadComplete={onUploadComplete}
                    context="artwork"
                  />
                </div>
                <div>
                  <ArtworkForm 
                    artwork={artwork} 
                    onClose={() => onOpenChange?.(false)} 
                  />
                </div>
              </div>

              {mediaTypes && (
                <div className="px-1">
                  <Separator className="my-4" />
                  <h3 className="text-lg font-semibold mb-4">Editions</h3>
                  <EditionTable
                    artwork_id={artwork.artwork_id}
                    onSelectEdition={setSelectedEdition}
                    mediaTypes={mediaTypes}
                  />
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedEdition} onOpenChange={(open) => !open && setSelectedEdition(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update Edition Details</DialogTitle>
          </DialogHeader>
          <EditionForm
            selectedEdition={selectedEdition}
            mediaTypes={mediaTypes}
            onCancel={() => setSelectedEdition(null)}
            onSubmit={handlePrintPriceUpdate}
            onPrintPriceChange={(value) => setSelectedEdition(prev => 
              prev ? { ...prev, print_price: value } : null
            )}
            onMediaTypeChange={(value) => setSelectedEdition(prev => 
              prev ? { ...prev, default_media: value ? parseInt(value) : null } : null
            )}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
